import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Headphones,
  X,
  Send,
  Clock,
  User,
  Shield,
  MessageSquare,
  CheckCircle,
  AlertCircle,
  Search,
  RefreshCw
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLiveSupport } from '../contexts/LiveSupportContext';

const AdminLiveSupport: React.FC = () => {
  const { userData } = useAuth();
  const {
    tickets,
    activeTicket,
    messages,
    joinTicket,
    leaveTicket,
    sendSupportMessage,
    adminCloseTicket
  } = useLiveSupport();

  const [messageText, setMessageText] = useState('');
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTicket, setSelectedTicket] = useState<any>(null);
  const [isJoining, setIsJoining] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!messageText.trim()) return;

    await sendSupportMessage(messageText);
    setMessageText('');
  };

  const handleCloseTicket = async () => {
    const ticketToClose = activeTicket || selectedTicket;
    if (!ticketToClose) return;
    setIsClosing(true);
    try {
      await adminCloseTicket(ticketToClose.id);
      setSelectedTicket(null);
    } catch (error) {
      console.error('Ticket kapatılamadı:', error);
    } finally {
      setIsClosing(false);
      setShowCloseConfirm(false);
    }
  };

  const handleJoinTicket = async () => {
    if (!selectedTicket) return;
    setIsJoining(true);
    try {
      await joinTicket(selectedTicket.id);
      setSelectedTicket(null); // Katıldıktan sonra selectedTicket'ı temizle
    } catch (error) {
      console.error('Ticket\'a katılınamadı:', error);
    } finally {
      setIsJoining(false);
    }
  };

  const handleSelectTicket = (ticket: any) => {
    // Eğer zaten aktif bir ticket varsa ve bu ticket'a zaten katılmışsak
    if (activeTicket?.id === ticket.id) {
      return; // Zaten bu ticket'tayız
    }
    // Eğer ticket'ın durumu 'active' ise ve biz atanmışsak, direkt mesajları göster
    if (ticket.status === 'active' && ticket.assignedAdmin === userData?.uid) {
      joinTicket(ticket.id);
    } else {
      // Değilse sadece seç (preview modunda)
      setSelectedTicket(ticket);
    }
  };

  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('tr-TR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'waiting':
        return (
          <span className="flex items-center space-x-1 text-yellow-400 text-xs bg-yellow-500/10 px-2 py-1 rounded-full">
            <Clock className="w-3 h-3" />
            <span>Bekliyor</span>
          </span>
        );
      case 'active':
        return (
          <span className="flex items-center space-x-1 text-green-400 text-xs bg-green-500/10 px-2 py-1 rounded-full">
            <CheckCircle className="w-3 h-3" />
            <span>Aktif</span>
          </span>
        );
      default:
        return null;
    }
  };

  const filteredTickets = tickets.filter(ticket =>
    ticket.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ticket.subject.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const waitingTickets = filteredTickets.filter(t => t.status === 'waiting');
  const activeTickets = filteredTickets.filter(t => t.status === 'active');

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[calc(100vh-300px)] min-h-[500px]">
      {/* Ticket List */}
      <div className="lg:col-span-1 bg-dark-800/50 rounded-xl border border-dark-700/50 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-dark-700/50">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center space-x-2">
              <Headphones className="w-5 h-5 text-green-500" />
              <span>Destek Talepleri</span>
            </h3>
            <div className="flex items-center space-x-2">
              {waitingTickets.length > 0 && (
                <span className="bg-yellow-500 text-white text-xs px-2 py-1 rounded-full">
                  {waitingTickets.length} bekliyor
                </span>
              )}
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Kullanıcı veya konu ara..."
              className="w-full pl-10 pr-4 py-2 bg-dark-700/50 border border-dark-600/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
            />
          </div>
        </div>

        {/* Ticket List */}
        <div className="flex-1 overflow-y-auto p-2 space-y-2">
          {filteredTickets.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Aktif destek talebi yok</p>
            </div>
          ) : (
            <>
              {/* Waiting Tickets */}
              {waitingTickets.length > 0 && (
                <div className="mb-4">
                  <div className="text-xs text-yellow-400 font-medium px-2 mb-2">
                    Bekleyen ({waitingTickets.length})
                  </div>
                  {waitingTickets.map((ticket) => (
                    <motion.button
                      key={ticket.id}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleSelectTicket(ticket)}
                      className={`w-full p-3 rounded-lg text-left transition-colors mb-2 ${
                        activeTicket?.id === ticket.id || selectedTicket?.id === ticket.id
                          ? 'bg-green-500/20 border border-green-500/50'
                          : 'bg-dark-700/30 hover:bg-dark-700/50 border border-transparent'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-white font-medium text-sm">{ticket.username}</span>
                        {getStatusBadge(ticket.status)}
                      </div>
                      <p className="text-gray-400 text-xs truncate">{ticket.subject}</p>
                      <p className="text-gray-500 text-xs mt-1">{formatTime(ticket.createdAt)}</p>
                    </motion.button>
                  ))}
                </div>
              )}

              {/* Active Tickets */}
              {activeTickets.length > 0 && (
                <div>
                  <div className="text-xs text-green-400 font-medium px-2 mb-2">
                    Aktif ({activeTickets.length})
                  </div>
                  {activeTickets.map((ticket) => (
                    <motion.button
                      key={ticket.id}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleSelectTicket(ticket)}
                      className={`w-full p-3 rounded-lg text-left transition-colors mb-2 ${
                        activeTicket?.id === ticket.id || selectedTicket?.id === ticket.id
                          ? 'bg-green-500/20 border border-green-500/50'
                          : 'bg-dark-700/30 hover:bg-dark-700/50 border border-transparent'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-white font-medium text-sm">{ticket.username}</span>
                        {getStatusBadge(ticket.status)}
                      </div>
                      <p className="text-gray-400 text-xs truncate">{ticket.subject}</p>
                      <p className="text-gray-500 text-xs mt-1">
                        {ticket.assignedAdminName && `${ticket.assignedAdminName} ile görüşüyor`}
                      </p>
                    </motion.button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className="lg:col-span-2 bg-dark-800/50 rounded-xl border border-dark-700/50 flex flex-col overflow-hidden relative">
        {/* Close Confirmation Modal */}
        <AnimatePresence>
          {showCloseConfirm && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-dark-900/80 backdrop-blur-sm z-10 rounded-xl"
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="absolute inset-0 flex items-center justify-center z-20 p-4"
              >
                <div className="bg-dark-800 border border-dark-700 rounded-xl p-6 max-w-sm w-full shadow-2xl">
                  <h3 className="text-lg font-bold text-white mb-2">
                    Görüşmeyi Sonlandır
                  </h3>
                  <p className="text-gray-400 text-sm mb-6">
                    Bu destek talebini kapatmak istediğinizden emin misiniz? Görüşme geçmişe kaydedilecektir.
                  </p>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => setShowCloseConfirm(false)}
                      className="flex-1 py-2 px-4 bg-dark-700 hover:bg-dark-600 text-white rounded-lg transition-colors"
                    >
                      İptal
                    </button>
                    <button
                      onClick={handleCloseTicket}
                      disabled={isClosing}
                      className="flex-1 py-2 px-4 bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white rounded-lg transition-colors"
                    >
                      {isClosing ? 'Kapatılıyor...' : 'Sonlandır'}
                    </button>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {!activeTicket && !selectedTicket ? (
          <div className="flex-1 flex items-center justify-center text-gray-400">
            <div className="text-center">
              <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Görüşme başlatmak için sol taraftan bir talep seçin</p>
            </div>
          </div>
        ) : !activeTicket && selectedTicket ? (
          /* Preview Mode - Ticket seçildi ama henüz katılınmadı */
          <>
            {/* Chat Header */}
            <div className="flex items-center justify-between p-4 border-b border-dark-700/50">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-yellow-500 to-orange-500 flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="text-white font-medium">{selectedTicket.username}</h4>
                  <p className="text-gray-400 text-xs">{selectedTicket.subject}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {getStatusBadge(selectedTicket.status)}
              </div>
            </div>

            {/* Ticket Info */}
            <div className="px-4 py-2 bg-dark-700/30 border-b border-dark-700/50">
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-400">
                  İlk Mesaj: <span className="text-white">{selectedTicket.initialMessage.substring(0, 50)}...</span>
                </span>
                <span className="text-gray-500 flex items-center space-x-1">
                  <Clock className="w-3 h-3" />
                  <span>{formatDate(selectedTicket.createdAt)}</span>
                </span>
              </div>
            </div>

            {/* Preview Content */}
            <div className="flex-1 flex items-center justify-center p-4">
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-yellow-500/20 flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-yellow-500" />
                </div>
                <h3 className="text-white font-medium mb-2">Destek Talebi Bekliyor</h3>
                <p className="text-gray-400 text-sm mb-6">
                  Bu kullanıcı destek temsilcisi bekliyor.<br />
                  Görüşmeyi başlatmak için "Chat'i Al" butonuna tıklayın.
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="p-4 border-t border-dark-700/50">
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleJoinTicket}
                  disabled={isJoining}
                  className="flex-1 py-3 bg-green-500 hover:bg-green-600 disabled:opacity-50 text-white font-medium rounded-lg transition-colors flex items-center justify-center space-x-2"
                >
                  {isJoining ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>Bağlanıyor...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      <span>Chat'i Al</span>
                    </>
                  )}
                </button>
                <button
                  onClick={() => setShowCloseConfirm(true)}
                  className="flex-1 py-3 bg-red-500 hover:bg-red-600 text-white font-medium rounded-lg transition-colors flex items-center justify-center space-x-2"
                >
                  <X className="w-4 h-4" />
                  <span>Chat'i Kapat</span>
                </button>
              </div>
            </div>
          </>
        ) : activeTicket && (
          <>
            {/* Chat Header */}
            <div className="flex items-center justify-between p-4 border-b border-dark-700/50">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="text-white font-medium">{activeTicket.username}</h4>
                  <p className="text-gray-400 text-xs">{activeTicket.subject}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={leaveTicket}
                  className="p-2 text-gray-400 hover:text-white hover:bg-dark-700/50 rounded-lg transition-colors"
                  title="Görüşmeyi Bırak"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setShowCloseConfirm(true)}
                  className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                  title="Görüşmeyi Sonlandır"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Ticket Info */}
            <div className="px-4 py-2 bg-dark-700/30 border-b border-dark-700/50">
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-400">
                  İlk Mesaj: <span className="text-white">{activeTicket.initialMessage.substring(0, 50)}...</span>
                </span>
                <span className="text-gray-500 flex items-center space-x-1">
                  <Clock className="w-3 h-3" />
                  <span>{formatDate(activeTicket.createdAt)}</span>
                </span>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.senderRole === 'admin' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[70%] px-3 py-2 rounded-lg ${
                      msg.senderRole === 'admin'
                        ? 'bg-green-500 text-white'
                        : 'bg-dark-700/50 text-gray-100'
                    }`}
                  >
                    <div className={`text-xs mb-1 flex items-center space-x-1 ${
                      msg.senderRole === 'admin' ? 'text-green-100' : 'text-gray-400'
                    }`}>
                      {msg.senderRole === 'admin' ? (
                        <>
                          <Shield className="w-3 h-3" />
                          <span>{msg.senderName}</span>
                        </>
                      ) : (
                        <>
                          <User className="w-3 h-3" />
                          <span>{msg.senderName}</span>
                        </>
                      )}
                    </div>
                    <div className="text-sm">{msg.text}</div>
                    <div className="text-xs opacity-70 mt-1">
                      {formatTime(msg.timestamp)}
                    </div>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <form onSubmit={handleSendMessage} className="p-4 border-t border-dark-700/50">
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  placeholder="Mesajınızı yazın..."
                  className="flex-1 px-4 py-2 bg-dark-700/50 border border-dark-600/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                />
                <button
                  type="submit"
                  disabled={!messageText.trim()}
                  className="p-2 bg-green-500 hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminLiveSupport;
